/**
 * Shared gov-form framing constants (s133, F-2 / F-3b). Single source for the fill_reviewed
 * (court / tax) lawyer-review copy and the withheld-recreation map, imported by BOTH the executor
 * drawer (GovFormZone) and the Estate Documents surfaces — no forked copy strings (G7).
 */

// Shown wherever a fill_reviewed court/tax form is surfaced INSTEAD of a generated recreation:
// the executor-drawer GovFormZone blurb and the Estate Documents / Ontario export withhold copy.
export const FILL_REVIEWED_REVIEW_COPY =
  "This court form must be reviewed by a lawyer or licensed paralegal before it is filed. Have the details below ready so you can complete it with them.";

// GOVID-READINESS-PRIVATE-STATE-1 (s201, Option C ruled at s200). The Ontario readiness surface's
// THIRD state — a row that is present and named but whose value the client holds privately in their
// secure file. It is neither entered nor missing, and it counts in neither the numerator nor the
// denominator. 🟢 The nine Government-ID rows (SIN x6, Health Card x2, Driver's Licence x1) read this
// because `id_number` is Category A: the value never reaches the server, so no lookup could ever
// supply it. ONE string, read by BOTH renderers of the same data — the HTML page and the PDF export.
export const HELD_PRIVATELY_COPY = "Held privately in your secure file";

// estate-documents DocumentType → form_registry formId for the fill_reviewed forms served as the
// official blank form + lawyer-review copy INSTEAD of a downloadable recreation (D-132.9), until the
// court-forms build lands. Membership drives BOTH the Estate Documents withheld treatment AND the
// generate route's 409 backstop. s134 (F-8 narrow): 74G + TX19 added — they never had a generated
// recreation, but they belong in the complete catalog with the same official-link + review treatment.
export const WITHHELD_GENERATED_FORM_TYPES: Record<string, string> = {
  "form-74a": "74A",
  "form-74-1a": "74.1A",
  "form-74b": "74B",
  "form-74d": "74D",
  "form-74g": "74G",
};

// ── Draft-docx court forms (COURT-FORMS-BUILD, D-135) ────────────────────────────────────────────
// form_registry.formId → estate-documents DocumentType for the fill_reviewed COURT forms we serve as
// a fact-only .docx (raw-OOXML fill, D-134.7) behind an acknowledgment gate (D-134.6). The output is a
// clean, file-able form — the DRAFT watermark was removed s137 (D-137); the ack gate + disclaimer are
// the retained protections. These forms STAY in WITHHELD_GENERATED_FORM_TYPES (the standalone Estate Documents page
// + the PDF backstop keep showing the official-link + review copy — the drawer is the draft surface,
// D3). The generate route serves the .docx via a branch BEFORE the withhold 409. s135 spike = 74D;
// s136 added 74A + 74.1A (Phase 2 — shaded-cell fill of the Part 1 deceased grid + applicant caption;
// 74.1A shares 74A's grid/map, differing only in the total shaded-cell count). s136 also added the two
// thin-shell forms 74B (deceased-name caption + sworn service testimony) and 74G (deceased name in the
// caption + two "of the deceased" refs; renouncer/consenter-authored otherwise).
export const DRAFT_DOCX_FORMS: Record<string, string> = {
  "74A": "form-74a",
  "74.1A": "form-74-1a",
  "74B": "form-74b",
  "74D": "form-74d",
  "74G": "form-74g",
};
// Reverse view for the server generate route (DocumentType membership test).
export const DRAFT_DOCX_DOC_TYPES: ReadonlySet<string> = new Set(Object.values(DRAFT_DOCX_FORMS));

// estate-documents DocumentType → form_registry formId, for the Phase-6b gap engine (s146, D-143.6):
// resolves which FORM_FIELD_TIERS / FORM_WRITEBACK_MAP entry a generation request maps to. Covers the
// docx court forms (reverse of DRAFT_DOCX_FORMS), the real_fill TX19, and the generated EIR prep-sheet.
// Returns null for a DocumentType with no Phase-6b classification (the federal real_fill forms, the
// app-generated internal docs) — those never gap-detect and generate exactly as before.
const DOC_TYPE_TO_FORM_ID: Record<string, string> = {
  ...Object.fromEntries(Object.entries(DRAFT_DOCX_FORMS).map(([formId, docType]) => [docType, formId])),
  "tx19": "TX19",
  "ontario-estate-info-return": "EIR",
  // s147 🔴 the 4 federal real_fill forms — the ROUTE uses THIS map to resolve formId for the gap/
  // prompt/writeback gate (PHASE_6B_ACTIVE_FORMS). They were missing here (present only in
  // form-field-maps DOCTYPE_TO_FORM_ID, which drives the fill), so activation was inert — no 422, no
  // T2 render. A DOC_TYPE↔formId coverage guard (doctype-form-id-coverage.test.ts) now pins the two.
  "cpp-death-benefit": "ISP-1200",
  "cpp-oas-cancellation": "ISP-1201",
  "cpp-survivors-pension": "ISP-1300",
  "cra-notification": "CRA-NOD",
};
export function documentTypeToFormId(documentType: string): string | null {
  return DOC_TYPE_TO_FORM_ID[documentType] ?? null;
}

// Ack-gated forms (D-137, s137) — the pre-download acknowledgment (form_draft_ack) applies to EVERY
// lawyer-gated form regardless of tech: the docx five above PLUS the real-pdf TX19 and the generated
// EIR prep-sheet. This drives supportsDraft / canDownloadDraft in the drawer. It is DELIBERATELY
// separate from DRAFT_DOCX_FORMS/DRAFT_DOCX_DOC_TYPES, which keep their docx-only ROUTING role (the
// generate route uses those to pick the .docx engine). Membership here = "show the ack gate", not
// "route to the docx engine". formIds (matches form_registry.formId).
export const ACK_GATED_FORMS: ReadonlySet<string> = new Set<string>([
  ...Object.keys(DRAFT_DOCX_FORMS), // 74A / 74.1A / 74B / 74D / 74G
  "TX19",                           // Phase 3 real-pdf fill (s137)
  "EIR",                            // Phase 3 generated prep-sheet (s137)
]);

// Generated forms that are DOWNLOADED from the task drawer (formId → estate-documents DocumentType).
// These aren't real-pdf fills (no FORM_CONFIGS) and aren't docx — they're app-generated PDFs served
// via the scratch generate path. The drawer resolves the download DocumentType through this after the
// real-fill + docx maps. EIR = the Ontario prep-sheet (D-137, s137).
// s149 SEAM-FIX-1: renamed intent — this maps a GENERATED formId → its DocumentType so the drawer can
// resolve a download. Ack-gating is SEPARATE (ACK_GATED_FORMS): EIR is gated, the two ServiceOntario
// cancellation notices are NOT (reviewGate "none") — they download directly via canDownloadGenerated.
export const GENERATED_ACK_DOC_TYPES: Record<string, string> = {
  "EIR": "ontario-estate-info-return",
  "ON-DL-CANCEL": "drivers-licence-cancellation",
  "OHIP-CANCEL": "health-card-cancellation",
};

// Acknowledgment-dialog copy (D-134.6). Single source — imported by the executor drawer's AckDialog.
// The download is a clean, file-able form (the DRAFT watermark was removed s137, D-137); the ack gate
// + disclaimer are the retained protections, so the copy no longer references a draft/watermark step.
// s146 (D-143.6): body amended to note that any details the executor typed in when preparing the form
// are their OWN answers (not the app's determination) — a lawyer/paralegal must still confirm them.
// // COUNSEL-REVIEW — wording confirmed/narrowed at counsel agenda item 1.
export const DRAFT_ACK_COPY = {
  heading: "Before you download this form",
  body:
    "We've filled in the basic facts from the estate plan — names, dates, and addresses. Any details " +
    "you were asked to type in are your own answers, not our determination. A lawyer or licensed " +
    "paralegal must review this whole form before it's filed with the court. Some parts need their " +
    "judgment, and filing an incorrect form can delay the estate or create personal liability for you.", // COUNSEL-REVIEW
  checkbox: "I understand this form must be reviewed by a lawyer or licensed paralegal before filing.",
  confirmLabel: "Download form",
  cancelLabel: "Cancel",
} as const;

// BENEFICIARY-FILL-1 (s156, ruling 2): a per-form advisory line appended to the acknowledgment gate.
// 74B is a sworn affidavit of service — who was / wasn't served is the executor's own legal declaration,
// and a not-served entitled person is a lawyer question. Keyed by formId; absent → no extra line.
export const DRAFT_ACK_ADVISORY_BY_FORM: Readonly<Record<string, string>> = {
  "74B":
    "Form 74B is a sworn affidavit: the statements about who was and was not served are your own legal " +
    "declarations. If anyone entitled to notice was not served, consult a lawyer or licensed paralegal " +
    "before filing.", // COUNSEL-REVIEW
};
